/* The property types, as one list.
 *
 * WHY THIS FILE EXISTS. The site had TWO hardcoded type→icon maps, in Home
 * and in ListProperty, each naming four of the five types that actually have
 * listings. استوديو was in neither, so the home grid rendered a tile with a
 * hole in it and the wizard offered a choice with no picture — the defect
 * Mark reported. Two copies of a list is one copy too many; this is the list.
 *
 * WHAT IT IS AND IS NOT. These are the DEFAULTS — the standard Egyptian set,
 * with the icon, category and applicable fields each type ships with. Once a
 * market has rows in `property_types`, the database wins and this file is
 * only the seed those rows were made from. It is not a second source of
 * truth at runtime; it is where the first run gets its truth.
 *
 * `legacy` is the string already sitting in `listings.type`. It is how an
 * existing listing keeps its type through all of this: nothing rewrites that
 * column, so the seed has to know the words it already contains.
 */
import type { TypeIcon } from "./typeIcons";

/** The fields a listing can carry. Spelled exactly as the database and the
 *  `property_types.fields` array spell them, so nothing has to translate
 *  between the two. `floor` and `land_area` are the additive columns
 *  migration 007 adds; the rest have been on `listings` from the start. */
export const FIELD_KEYS = [
  "area", "beds", "baths", "kitchens", "furnished", "floor", "land_area",
] as const;
export type FieldKey = (typeof FIELD_KEYS)[number];

export type Category = "residential" | "commercial" | "land";

export type PropertyTypeSeed = {
  slug: string;
  legacy: string | null;
  nameAr: string;
  nameEn: string;
  icon: TypeIcon;
  category: Category;
  fields: readonly FieldKey[];
};

/* The shapes that repeat, named once. A flat is a flat whether it is a
 * penthouse or a studio; what differs is spelled out per type below. */
const HOME = ["area", "beds", "baths", "kitchens", "furnished", "floor"] as const;
const HOUSE = ["area", "beds", "baths", "kitchens", "furnished", "land_area"] as const;
const COMMERCIAL = ["area", "baths", "furnished", "floor"] as const;

/** Bassem's standard Egypt set, in the order he listed it. That order is the
 *  seed's sort order — the site's order is whatever Admin drags it into. */
export const SEED_TYPES: readonly PropertyTypeSeed[] = [
  { slug: "apartment", legacy: "Apartment", nameAr: "شقة", nameEn: "Apartment",
    icon: "type-apartment", category: "residential", fields: HOME },
  { slug: "villa", legacy: "Villa", nameAr: "فيلا", nameEn: "Villa",
    icon: "type-villa", category: "residential", fields: HOUSE },
  { slug: "duplex", legacy: "Duplex", nameAr: "دوبلكس", nameEn: "Duplex",
    icon: "type-duplex", category: "residential", fields: HOME },
  { slug: "townhouse", legacy: "Townhouse", nameAr: "تاون هاوس", nameEn: "Townhouse",
    icon: "type-townhouse", category: "residential", fields: HOUSE },
  { slug: "twinhouse", legacy: "Twin House", nameAr: "توين هاوس", nameEn: "Twin House",
    icon: "type-twinhouse", category: "residential", fields: HOUSE },
  { slug: "penthouse", legacy: "Penthouse", nameAr: "بنتهاوس", nameEn: "Penthouse",
    icon: "type-penthouse", category: "residential", fields: HOME },
  /* One room: it has no separate bedroom to count, which is the whole idea of
   * a studio. Asking "how many rooms?" would invite the answer 0 or 1 and
   * mean neither. */
  { slug: "studio", legacy: "Studio", nameAr: "استوديو", nameEn: "Studio",
    icon: "type-studio", category: "residential",
    fields: ["area", "baths", "furnished", "floor"] },
  { slug: "chalet", legacy: "Chalet", nameAr: "شاليه", nameEn: "Chalet",
    icon: "type-chalet", category: "residential",
    fields: ["area", "beds", "baths", "furnished"] },
  /* Land has no rooms, no baths and nothing to furnish — Bassem's own
   * example of what applicable fields are for. */
  { slug: "land", legacy: "Land", nameAr: "أرض", nameEn: "Land",
    icon: "type-land", category: "land", fields: ["land_area"] },
  { slug: "retail", legacy: "Retail", nameAr: "محل تجاري", nameEn: "Retail Shop",
    icon: "type-retail", category: "commercial", fields: COMMERCIAL },
  { slug: "office", legacy: "Office", nameAr: "مكتب إداري", nameEn: "Office",
    icon: "type-office", category: "commercial", fields: COMMERCIAL },
  { slug: "clinic", legacy: "Clinic", nameAr: "عيادة", nameEn: "Clinic",
    icon: "type-clinic", category: "commercial", fields: COMMERCIAL },
  /* A warehouse is a volume, not a set of rooms. */
  { slug: "warehouse", legacy: "Warehouse", nameAr: "مخزن", nameEn: "Warehouse",
    icon: "type-storage", category: "commercial", fields: ["area", "floor"] },
  { slug: "building", legacy: "Building", nameAr: "مبنى كامل", nameEn: "Whole Building",
    icon: "type-building", category: "residential",
    fields: ["area", "land_area", "beds", "baths"] },
];

/* The legacy string is what a listing actually stores, so that is what the
 * site looks a type up by. Built from the list above so it cannot drift. */
const BY_LEGACY = new Map(
  SEED_TYPES.filter((t) => t.legacy).map((t) => [t.legacy!.toLowerCase(), t]),
);
const BY_SLUG = new Map(SEED_TYPES.map((t) => [t.slug, t]));

/** Find a seed type by whatever the caller happens to be holding — the value
 *  from `listings.type`, or a slug. */
export const seedType = (v: string | null | undefined): PropertyTypeSeed | null => {
  const k = (v ?? "").trim().toLowerCase();
  if (!k) return null;
  return BY_LEGACY.get(k) ?? BY_SLUG.get(k) ?? null;
};

/** The icon file for a type, or null. Null renders nothing rather than
 *  borrowing another type's picture: a villa icon on an unknown type is a
 *  claim about the property that nobody made. */
export const seedIcon = (v: string | null | undefined): TypeIcon | null =>
  seedType(v)?.icon ?? null;
